import type {
  EntrDataTransport,
  EntrPrintedPhotoType,
} from '@/transport/types';
import { stableHash } from './controllerUtils';

export interface PrintedCoverageWritebackObserver {
  addBreadcrumb(
    category: string,
    message: string,
    data?: Record<string, unknown>
  ): void;
  captureException(error: unknown, tag: string): void;
  captureMessage(
    message: string,
    level: 'error' | 'warning',
    extra?: Record<string, unknown>
  ): void;
}

export interface PrintedCoverageWritebackQueue {
  drain(): void;
  stop(): void;
  recordDelta(input: {
    coverageKey: string;
    groupId: string;
    ticketCode?: string;
    confirmedCount: number;
    recordedCount: number;
    photoType?: EntrPrintedPhotoType;
  }): Promise<number>;
}

interface WritebackItem {
  // OUR ledger key (backend entr_group_id) — the source-of-truth identity. Set
  // for every item this build enqueues; optional only because legacy items
  // queued before groupId shipped carry ticketCode alone (backend resolves
  // their group via qr on drain).
  groupId?: string;
  // Only present for ticket sessions; sent so the backend can also forward the
  // print to Fever best-effort. Legacy (pre-groupId) items carry only this.
  ticketCode?: string;
  amount: number;
  attemptedAt: number;
  attempts: number;
  photoType?: EntrPrintedPhotoType;
  // Stable per-print idempotency key forwarded to /photo_printed and reused
  // verbatim on every retry, so the backend PK-idempotently dedups a retried
  // writeback. Optional so legacy items still drain (backend resolves a
  // fallback identity via qr).
  eventKey?: string;
}

interface PrintedCoverageItem {
  key: string;
  count: number;
  updatedAt: number;
}

const WRITEBACK_STORAGE_KEY = 'entr_kiosk.pending_writebacks';
const PRINTED_COVERAGE_STORAGE_KEY = 'entr_kiosk.printed_coverage';
// Retry forever — a confirmed-print writeback is never dropped on an attempt
// count, because a dropped writeback means OUR consumption ledger permanently
// under-counts a customer's prints (they paid, we printed, our source of truth
// never learns — a revenue leak). The schedule ramps then caps;
// scheduleWritebackRetry clamps the index so attempts past the array length
// keep retrying at the final (5min) delay indefinitely.
const WRITEBACK_BACKOFFS_MS = [10_000, 60_000, 300_000, 300_000, 300_000];
const PRINTED_COVERAGE_TTL_MS = 30 * 60 * 1000;

const DROPPED_WRITEBACKS = new Map<string, [string, 'error' | 'warning']>([
  // A validation-refused writeback can never succeed on retry — drop it. Post
  // the ledger cutover this LOSES A REAL CONSUMPTION (our source of truth
  // under-counts, a revenue leak), so it is an error, not a warning; kept only
  // to stop a permanently-poisoned payload blocking every later writeback.
  [
    'rejected',
    ['ENTR writeback dropped: rejected by backend validation', 'error'],
  ],
  // 404 is now unreachable via the real backend — a Fever qr-not-found is
  // swallowed to 200 server-side (best-effort notify). Kept one deploy cycle
  // as defense against an in-flight old backend; remove in a follow-up.
  [
    'not-found',
    ['ENTR writeback dropped: ticket not found upstream', 'warning'],
  ],
]);

type StorageFault = (error: unknown, phase: 'read' | 'parse' | 'write') => void;

export function createPrintedCoverageWritebackQueue(
  recordPhotoPrinted: EntrDataTransport['recordPhotoPrinted'],
  observer: PrintedCoverageWritebackObserver
): PrintedCoverageWritebackQueue {
  const timers = new Set<number>();

  async function recordDelta(input: {
    coverageKey: string;
    groupId: string;
    ticketCode?: string;
    confirmedCount: number;
    recordedCount: number;
    photoType?: EntrPrintedPhotoType;
  }): Promise<number> {
    const confirmedCount = Math.max(0, Math.floor(input.confirmedCount));
    const recordedCount = Math.max(
      input.recordedCount,
      readPrintedCoverageCount(input.coverageKey)
    );
    const delta = confirmedCount - recordedCount;
    if (delta <= 0) return recordedCount;

    // Key each incremental delta by its cumulative endpoint so partial + final
    // writebacks stay distinct (both must record), while a RETRY of the same
    // delta reuses the same key and the backend dedups it. Hashed so a
    // multi-photo selection's long coverageKey can't blow past the backend's
    // 255-char event_key limit (which would 422 the writeback and loop forever).
    const eventKey = `entr-printed-${stableHash(`${input.coverageKey}:${confirmedCount}`)}`;
    await recordPrintedCoverage(delta, input.groupId, eventKey, {
      ...(input.ticketCode ? { ticketCode: input.ticketCode } : {}),
      ...(input.photoType ? { photoType: input.photoType } : {}),
    });
    writePrintedCoverageCount(input.coverageKey, confirmedCount);
    return confirmedCount;
  }

  async function recordPrintedCoverage(
    printedCount: number,
    groupId: string,
    eventKey: string,
    extra: { ticketCode?: string; photoType?: EntrPrintedPhotoType }
  ): Promise<void> {
    const amount = Math.max(1, Math.floor(printedCount));
    const item: WritebackItem = {
      groupId,
      amount,
      attemptedAt: Date.now(),
      attempts: 0,
      eventKey,
      ...(extra.ticketCode ? { ticketCode: extra.ticketCode } : {}),
      ...(extra.photoType ? { photoType: extra.photoType } : {}),
    };
    upsertQueuedWriteback(item, observer);
    await runWriteback(item);
  }

  async function runWriteback(item: WritebackItem): Promise<void> {
    const nextAttempts = item.attempts + 1;
    let ok = false;
    let failureReason: string | undefined;
    try {
      const result = await recordPhotoPrinted({
        ...(item.groupId ? { groupId: item.groupId } : {}),
        ...(item.ticketCode ? { ticketCode: item.ticketCode } : {}),
        amount: item.amount,
        eventKey: writebackEventKey(item),
        ...(item.photoType ? { photoType: item.photoType } : {}),
      });
      ok = result.ok;
      if (!result.ok) failureReason = result.reason;
    } catch (error) {
      observer.captureException(error, 'record_photo_printed');
      failureReason = 'network-error';
    }

    observer.addBreadcrumb(
      'entr.print',
      'Printed coverage writeback completed',
      {
        ok,
        amount: item.amount,
        attempts: nextAttempts,
        reason: ok ? undefined : failureReason,
      }
    );

    if (ok) {
      removeQueuedWriteback(item, observer);
      return;
    }

    const dropped = failureReason && DROPPED_WRITEBACKS.get(failureReason);
    if (dropped) {
      removeQueuedWriteback(item, observer);
      observer.captureMessage(dropped[0], dropped[1], {
        groupId: item.groupId,
        amount: item.amount,
        attempts: nextAttempts,
      });
      return;
    }

    const updated: WritebackItem = { ...item, attempts: nextAttempts };
    upsertQueuedWriteback(updated, observer);
    scheduleWritebackRetry(updated);
  }

  function scheduleWritebackRetry(item: WritebackItem): void {
    const delayIndex = Math.min(
      item.attempts,
      WRITEBACK_BACKOFFS_MS.length - 1
    );
    const delayMs = WRITEBACK_BACKOFFS_MS[delayIndex] ?? 300_000;
    const timer = window.setTimeout(() => {
      timers.delete(timer);
      void runWriteback(item);
    }, delayMs);
    timers.add(timer);
  }

  function drain(): void {
    const queue = readWritebackQueue(observer);
    if (queue.length === 0) return;
    observer.addBreadcrumb('entr.print', 'Draining writeback queue', {
      pending: queue.length,
    });
    for (const item of queue) {
      scheduleWritebackRetry(item);
    }
  }

  function stop(): void {
    for (const timer of timers) window.clearTimeout(timer);
    timers.clear();
  }

  return { drain, stop, recordDelta };
}

function readStoredArray<T>(
  key: string,
  isValid: (value: unknown) => value is T,
  onFault?: StorageFault
): T[] {
  if (typeof window === 'undefined') return [];
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch (error) {
    onFault?.(error, 'read');
    return [];
  }
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) throw new Error('queue is not an array');
    return parsed.filter(isValid);
  } catch (error) {
    onFault?.(error, 'parse');
    try {
      window.localStorage.removeItem(key);
    } catch {
      // best-effort cleanup
    }
    return [];
  }
}

function writeStoredArray<T>(
  key: string,
  items: T[],
  options: { removeWhenEmpty?: boolean; onFault?: StorageFault } = {}
): void {
  if (typeof window === 'undefined') return;
  try {
    if (options.removeWhenEmpty && items.length === 0) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(items));
  } catch (error) {
    options.onFault?.(error, 'write');
  }
}

function writebackFault(
  observer: PrintedCoverageWritebackObserver
): StorageFault {
  return (error, phase) =>
    observer.captureException(error, `writeback_${phase}`);
}

function readWritebackQueue(
  observer: PrintedCoverageWritebackObserver
): WritebackItem[] {
  return readStoredArray(
    WRITEBACK_STORAGE_KEY,
    isValidWritebackItem,
    writebackFault(observer)
  );
}

function writeWritebackQueue(
  queue: WritebackItem[],
  observer: PrintedCoverageWritebackObserver
): void {
  writeStoredArray(WRITEBACK_STORAGE_KEY, queue, {
    removeWhenEmpty: true,
    onFault: writebackFault(observer),
  });
}

// Identity of a queued writeback = its per-print eventKey (unique per print).
// Legacy items queued before eventKey shipped get a stable fallback derived
// from their invariant fields, so every reference to the same stored item
// resolves the same key and upsert/remove match it. Keying on eventKey (not
// ticketCode+attemptedAt) is essential for browse: ticketCode is undefined
// there, so the old key collapsed to (undefined, attemptedAt) and cross-matched
// sibling writebacks, evicting a real row (a ledger under-count).
function writebackEventKey(item: WritebackItem): string {
  return (
    item.eventKey ??
    `entr-printed-legacy-${stableHash(
      `${item.ticketCode ?? ''}:${item.amount}:${item.attemptedAt}`
    )}`
  );
}

function upsertQueuedWriteback(
  item: WritebackItem,
  observer: PrintedCoverageWritebackObserver
): void {
  const queue = readWritebackQueue(observer);
  const key = writebackEventKey(item);
  const index = queue.findIndex((entry) => writebackEventKey(entry) === key);
  if (index >= 0) {
    queue[index] = item;
  } else {
    queue.push(item);
  }
  writeWritebackQueue(queue, observer);
}

function removeQueuedWriteback(
  item: WritebackItem,
  observer: PrintedCoverageWritebackObserver
): void {
  const queue = readWritebackQueue(observer);
  const key = writebackEventKey(item);
  const next = queue.filter((entry) => writebackEventKey(entry) !== key);
  if (next.length === queue.length) return;
  writeWritebackQueue(next, observer);
}

function readPrintedCoverageCount(key: string): number {
  const queue = readPrintedCoverageQueue();
  return queue.find((item) => item.key === key)?.count ?? 0;
}

function writePrintedCoverageCount(key: string, count: number): void {
  const now = Date.now();
  const fresh = readPrintedCoverageQueue(now).filter(
    (item) => item.key !== key
  );
  fresh.push({ key, count, updatedAt: now });
  writePrintedCoverageQueue(fresh);
}

function readPrintedCoverageQueue(now = Date.now()): PrintedCoverageItem[] {
  return readStoredArray(
    PRINTED_COVERAGE_STORAGE_KEY,
    isValidPrintedCoverageItem
  ).filter((item) => now - item.updatedAt <= PRINTED_COVERAGE_TTL_MS);
}

// Printed coverage is best-effort dedupe only, thus a storage fault is ignored.
function writePrintedCoverageQueue(queue: PrintedCoverageItem[]): void {
  writeStoredArray(PRINTED_COVERAGE_STORAGE_KEY, queue);
}

function isValidWritebackItem(value: unknown): value is WritebackItem {
  if (!value || typeof value !== 'object') return false;
  const item = value as Record<string, unknown>;
  // Keep legacy ticketCode-only items (no groupId) so the backend can resolve
  // their group via qr on drain — the default, cross-deploy-loss-minimizing
  // behavior. Only items with NEITHER identity are unusable and dropped.
  const hasIdentity =
    typeof item.groupId === 'string' || typeof item.ticketCode === 'string';
  return (
    hasIdentity &&
    (item.groupId === undefined || typeof item.groupId === 'string') &&
    (item.ticketCode === undefined || typeof item.ticketCode === 'string') &&
    typeof item.amount === 'number' &&
    typeof item.attemptedAt === 'number' &&
    typeof item.attempts === 'number' &&
    (item.eventKey === undefined || typeof item.eventKey === 'string') &&
    (item.photoType === undefined ||
      item.photoType === 'group_photo' ||
      item.photoType === 'portrait_photo')
  );
}

function isValidPrintedCoverageItem(
  value: unknown
): value is PrintedCoverageItem {
  if (!value || typeof value !== 'object') return false;
  const item = value as Record<string, unknown>;
  return (
    typeof item.key === 'string' &&
    typeof item.count === 'number' &&
    typeof item.updatedAt === 'number'
  );
}
